'use es6';

import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useEffect, useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import getLang from 'LearningCenterUI/utils/getLang';
import ApiError from '../../components/Errors/ApiError';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { HR } from 'foundations-components/transitional/components/HR';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import LearningPageHeader from './LearningPageHeader';
import TrackBody from './TrackBody';
import { fetchTrackByIdIfNeeded } from '../../actions/TrackActions';
import { trackIsLoading, getTrack } from '../../selectors/trackSelectors';
import LearningPageSidebar from './LearningPageSidebar';
import LearningPageMainSection from './LearningPageMainSection';
import { getSelectedLanguageFiltersFromRoute } from '../../selectors/routeSelectors';
import { getContentById } from '../../selectors/contentSelectors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isTrackDeprecationWarning } from '../../utils/utils';
import TrackDeprecatedDescription from '../../components/TrackCard/TrackDeprecatedDescription';
import { useAvailableLanguageOrFallback } from '../../features/contentLanguage';
import useWootricSurvey from '../../hooks/useWootricSurvey';
import { useFullTrack } from '../../features/tracks';
import { useTrackProgressById } from '../../features/userProgress';
import CourseNavigator from '../../components/Navigator/CourseNavigator';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useSetLastClickedRecommendationItemFromUrl } from '../../features/recommendations/recommendationsHooks';
import CourseUnavailableModal from '../../features/contentLanguage/components/CourseUnavailableModal';
import { useSingleLanguageRedirect } from '../../hooks/useSingleLangaugeRedirect';
import LearningContentInnerWrapper from '../../features/learningContent/components/LearningContentInnerWrapper';
import { usePageTitle } from '../../hooks/usePageTitle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TrackPageRouter = ({
  content,
  isLoading,
  fetchTrack,
  language,
  match,
  track
}) => {
  const {
    params: {
      trackId,
      lessonId,
      contentId
    }
  } = match;
  const {
    isMobile
  } = useMediaQuery();
  const shouldSkipSurvey = lessonId === 'exam';
  useWootricSurvey('CSAT', shouldSkipSurvey);
  const contentItem = content && content.get('name');
  const {
    languageToLoad,
    availableLanguagesLoading,
    availableLanguagesData
  } = useAvailableLanguageOrFallback({
    trackId,
    language
  });
  const {
    redirectUrl: singleLanguageRedirectUrl
  } = useSingleLanguageRedirect({
    contentId: Number(trackId),
    language: language.toUpperCase(),
    contentType: 'COURSE'
  });
  useEffect(() => {
    if (!availableLanguagesLoading) fetchTrack(trackId, languageToLoad);
  }, [trackId, fetchTrack, languageToLoad, availableLanguagesLoading]);
  const deprecationWarningDate = track && track.get('deprecationWarningDate');
  const deprecationFinalDate = track && track.get('deprecationFinalDate');
  const deprecationMessage = track && track.get('deprecationMessage');
  const deprecationCta = track && track.get('deprecationCta');
  const deprecated = track && track.get('deprecated');
  const showWarning = isTrackDeprecationWarning(deprecated, deprecationFinalDate, deprecationWarningDate);
  const {
    track: fullTrack,
    trackLoading: fullTrackLoading
  } = useFullTrack(Number(trackId), language);
  const {
    trackProgressById
  } = useTrackProgressById(Number(trackId));
  usePageTitle({
    i18nKey: 'learningCenter.dynamicPageTitles.trackName',
    options: {
      trackName: fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.name
    },
    skip: !(fullTrack !== null && fullTrack !== void 0 && fullTrack.name)
  });
  useSetLastClickedRecommendationItemFromUrl({
    id: trackId ? Number(trackId) : undefined,
    type: 'TRACK'
  });
  const trackAggregatedLessonContents = useMemo(() => fullTrack && fullTrack.lessons.reduce((prev, currentLesson) => {
    const newArray = prev;
    newArray.push(currentLesson.content);
    return newArray;
  }, []), [fullTrack]);
  const instructors = useMemo(() => {
    if (!trackAggregatedLessonContents) {
      return [];
    }
    const instructorsById = {};
    trackAggregatedLessonContents.forEach(lesson => {
      lesson.forEach(asset => {
        if (asset.professors) {
          asset.professors.forEach(professor => {
            instructorsById[professor.id] = professor;
          });
        }
      });
    });
    return Object.values(instructorsById);
  }, [trackAggregatedLessonContents]);
  const instructorIds = instructors.map(instructor => instructor.id);
  const ratingsAdditionalTrackingData = fullTrack && {
    contentLanguage: fullTrack.language,
    contentName: fullTrack.name,
    creatorHubId: fullTrack.hubId,
    hsSoftwareFilters: fullTrack.hsSoftwareFilters,
    professorId: instructorIds,
    subCategory: fullTrack.subCategories,
    useCase: fullTrack.useCases
  };
  const assetProgress = useMemo(() => {
    if (!trackProgressById || !trackProgressById.progress) {
      return {};
    }
    const progressMap = {};
    trackProgressById.progress.forEach(lesson => {
      if (lesson.contentProgress && Array.isArray(lesson.contentProgress)) {
        lesson.contentProgress.forEach(contentProgress => {
          progressMap[contentProgress.contentId] = contentProgress.progressType;
        });
      }
    });
    if (trackProgressById.introVideoProgress) {
      progressMap[trackProgressById.introVideoProgress.contentId] = trackProgressById.introVideoProgress.progressType;
    }
    return progressMap;
  }, [trackProgressById]);
  const isLoadingAndNoTrackData = (isLoading || availableLanguagesLoading || fullTrackLoading) && (!track || !fullTrack || track.get('language', '').toUpperCase() !== language);
  const navigator = fullTrack && /*#__PURE__*/_jsx(CourseNavigator, {
    activeContentId: contentId,
    activeLessonId: lessonId,
    exam: fullTrack.exam && {
      id: fullTrack.exam.id,
      duration: fullTrack.exam.durationMinutes,
      totalQuestions: fullTrack.exam.length
    },
    introVideo: fullTrack.introVideo,
    language: language,
    lessons: fullTrack.lessons.map(lesson => ({
      id: lesson.id,
      name: lesson.name,
      duration: lesson.duration,
      useCases: lesson.useCases,
      assets: lesson.content.map(asset => ({
        id: asset.id,
        name: asset.name,
        duration: asset.duration,
        type: asset.contentType,
        totalQuestions: asset.length,
        linkType: asset.linkType,
        completed: assetProgress[asset.id] === 'COMPLETED'
      }))
    })),
    trackId: Number(trackId)
  });
  if (!availableLanguagesLoading && availableLanguagesData.length > 0 && !availableLanguagesData.includes(language.toUpperCase())) {
    return /*#__PURE__*/_jsx(CourseUnavailableModal, {
      availableLanguages: availableLanguagesData
    });
  }
  if (singleLanguageRedirectUrl) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: {
        pathname: singleLanguageRedirectUrl,
        search: window.location.search
      }
    });
  }

  // Check for course redirectUrl - redirect to another URL if course is unpublished/deleted
  if (fullTrack !== null && fullTrack !== void 0 && fullTrack.redirectUrl) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: {
        pathname: fullTrack.redirectUrl,
        search: window.location.search
      }
    });
  }
  const trackHasReturnedWithError = !isLoading && track && track.get('hasError');
  if (trackHasReturnedWithError) {
    return /*#__PURE__*/_jsx(ApiError, {
      contentType: 'COURSE',
      error: track.get('recentError'),
      marker404: "COURSE_VIEW_API_404",
      markerDefault: "COURSE_VIEW_API_ERROR"
    });
  }
  return /*#__PURE__*/_jsx(LearningContentInnerWrapper, {
    children: /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs(View, {
        insetBlockStart: "0px",
        position: "sticky",
        zIndex: 3,
        children: [showWarning && !!deprecationMessage && /*#__PURE__*/_jsx(Alert, {
          "data-test-id": "track-deprecated-warning-alert",
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.tracks.deprecations.deprecationWarnTitle"
          }),
          type: "info",
          children: /*#__PURE__*/_jsx(TrackDeprecatedDescription, {
            compressed: true,
            cta: deprecationCta,
            message: deprecationMessage,
            trackId: Number(trackId)
          })
        }), !isLoadingAndNoTrackData && /*#__PURE__*/_jsx(LearningPageHeader, {
          ContentName: content && content.get('quizType') ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.lessonNavigator.quiz"
          }) : contentItem,
          additionalTrackingData: ratingsAdditionalTrackingData,
          availableLanguages: fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.availableLanguages,
          defaultBackLink: {
            pathname: '/'
          },
          language: language,
          languageLinkedContent: fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.languageLinkedContent,
          trackId: trackId
        })]
      }), isLoadingAndNoTrackData ? /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        minHeight: 400
      }) : /*#__PURE__*/_jsxs(Flex, {
        borderBlockEndStyle: !isMobile ? 'solid' : undefined,
        borderColor: "border-core-subtle",
        borderEndEndRadius: "400",
        borderEndStartRadius: "400",
        borderInlineStyle: !isMobile ? 'solid' : undefined,
        borderWidth: !isMobile ? '100' : undefined,
        flexDirection: isMobile ? 'column-reverse' : 'row',
        overflow: "clip",
        children: [!isMobile ? /*#__PURE__*/_jsx(LearningPageSidebar, {
          id: "sidebarNavigator",
          withSubNav: 1,
          children: navigator
        }) : /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(HR, {
            distance: "flush"
          }), navigator]
        }), /*#__PURE__*/_jsx(LearningPageMainSection, {
          minInlineSize: "0",
          children: /*#__PURE__*/_jsx(TrackBody, {
            contentId: contentId,
            language: language,
            lessonId: lessonId,
            trackId: trackId
          })
        })]
      })]
    })
  });
};
TrackPageRouter.propTypes = {
  content: PropTypes.object,
  fetchRecommendedTracks: PropTypes.func,
  fetchTrack: PropTypes.func,
  isLastLessonInTrack: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  language: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      contentId: PropTypes.string,
      lessonId: PropTypes.string,
      trackId: PropTypes.string
    }).isRequired
  }).isRequired,
  track: ImmutablePropTypes.map
};
const mapStateToProps = () => {
  return (state, ownProps) => {
    const {
      match: {
        params: {
          trackId,
          contentId
        }
      }
    } = ownProps;
    const selectedTrack = getTrack(state, {
      trackId
    });
    const content = getContentById(state, contentId);
    const selectedTrackLanguage = selectedTrack ? selectedTrack.get('language') || getLang() : getLang();
    return {
      content,
      isLoading: trackIsLoading(state, {
        trackId
      }),
      track: selectedTrack,
      language: getSelectedLanguageFiltersFromRoute(state, ownProps).first() || selectedTrackLanguage
    };
  };
};
const mapDispatchToProps = dispatch => {
  return bindActionCreators({
    fetchTrack: fetchTrackByIdIfNeeded
  }, dispatch);
};
export default connect(mapStateToProps, mapDispatchToProps)(TrackPageRouter);